import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FiUser, FiX, FiMenu, FiLogOut, FiHelpCircle } from "react-icons/fi";

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Help & Support States
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("report"); // "report" or "feedback"
  const [issueType, setIssueType] = useState("WhatsApp Connection");
  const [issueDescription, setIssueDescription] = useState("");
  const [screenshot, setScreenshot] = useState(null); // { filename, mimetype, data }
  const [rating, setRating] = useState(5);
  const [feedbackText, setFeedbackText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [successState, setSuccessState] = useState(false);

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        setScreenshot({
          filename: file.name,
          mimetype: file.type,
          data: reader.result.split(',')[1] // base64 string
        });
      };
    }
  };

  const handleSupportSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const loggedInUser = localStorage.getItem("username") || "User";
    const loggedInEmail = localStorage.getItem("email") || "";

    const payload = {
      username: loggedInUser,
      email: loggedInEmail,
      type: activeTab === "report" ? "issue" : "feedback",
      issueType: activeTab === "report" ? issueType : undefined,
      description: activeTab === "report" ? issueDescription : feedbackText,
      rating: activeTab === "feedback" ? rating : undefined,
      screenshot: activeTab === "report" ? screenshot : undefined
    };

    try {
      const response = await fetch("https://goyee.onrender.com/api/support", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSuccessState(true);
        // Clear forms
        setIssueDescription("");
        setScreenshot(null);
        setFeedbackText("");
        setRating(5);
      } else {
        alert("⚠️ Failed to submit request. Please try again.");
      }
    } catch (err) {
      console.error(err);
      alert("⚠️ Error submitting support request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = () => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span
          key={i}
          onClick={() => setRating(i)}
          className="cursor-pointer text-2xl sm:text-3xl transition-all duration-150 transform hover:scale-125 select-none"
          style={{ color: i <= rating ? "#F59E0B" : "rgba(255,255,255,0.2)" }}
        >
          ★
        </span>
      );
    }
    return <div className="flex gap-2 justify-center my-2">{stars}</div>;
  };

  const renderSuccess = () => (
    <div className="text-center py-8 space-y-4">
      <div className="w-16 h-16 bg-[#25D366]/10 text-[#25D366] rounded-full flex items-center justify-center mx-auto border border-[#25D366]/30 animate-bounce">
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
        </svg>
      </div>
      <h3 className="text-xl font-bold text-white font-['Space_Grotesk']">✅ Thank you!</h3>
      <p className="text-sm text-white/70">Your request has been submitted successfully.</p>
      <p className="text-xs text-white/50">Our support team will contact you soon.</p>
      <button
        onClick={() => {
          setSuccessState(false);
          setHelpModalOpen(false);
        }}
        className="mt-6 px-6 py-2.5 bg-[#25D366] text-white text-sm font-semibold rounded-xl hover:bg-[#128C4A] transition-colors"
      >
        Close
      </button>
    </div>
  );

  useEffect(() => {
    const checkLogin = () => {
      const loggedIn = localStorage.getItem("isLoggedIn") === "true";
      setIsLoggedIn(loggedIn);
    };

    checkLogin();

    window.addEventListener("storage", checkLogin);
    window.addEventListener("loginStatusChanged", checkLogin);

    return () => {
      window.removeEventListener("storage", checkLogin);
      window.removeEventListener("loginStatusChanged", checkLogin);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleOpenSupport = () => {
      setHelpModalOpen(true);
    };
    window.addEventListener("openSupportModal", handleOpenSupport);
    return () => {
      window.removeEventListener("openSupportModal", handleOpenSupport);
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    const handleKey = (e) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
      }
    };

    window.addEventListener("keydown", handleKey);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKey);
    };
  }, [mobileOpen]);

  const handleLogout = () => {
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("username");

    window.dispatchEvent(new Event("loginStatusChanged"));

    setIsLoggedIn(false);
    setMobileOpen(false);

    if (location.pathname.includes("admin")) {
      navigate("/goyee-admin-login");
    } else {
      navigate("/");
    }
  };

  /*
   * Connect button:
   * Uses the existing WhatsAppAuth QR scanner.
   * No new QR scanner is created.
   */
  const handleConnect = () => {
    setMobileOpen(false);

    // If already on the WhatsAppAuth page,
    // directly trigger the existing QR flow.
    if (location.pathname === "/") {
      window.dispatchEvent(new Event("gy:connect-device"));
      return;
    }

    // Otherwise navigate to the existing WhatsAppAuth page.
    navigate("/");

    // Wait for the page to mount, then trigger
    // the existing QR connection flow.
    setTimeout(() => {
      window.dispatchEvent(new Event("gy:connect-device"));
    }, 500);
  };

  /*
   * Messages menu item:
   * Reuses the existing WhatsAppAuth chat/messages panel (the same
   * screen shown right after a successful QR scan). No new page is
   * created — this just reveals/re-opens that existing panel and,
   * if the WhatsApp session is still valid, does so without requiring
   * the user to scan the QR code again.
   */
  const handleMessagesClick = () => {
    setMobileOpen(false);

    // If already on the WhatsAppAuth page, directly trigger the
    // existing messages panel to open (or resume) in place.
    if (location.pathname === "/") {
      window.dispatchEvent(new Event("gy:open-messages"));
      return;
    }

    // Otherwise navigate to the existing WhatsAppAuth page, passing
    // along router state so it can render the correct view on its very
    // first paint instead of briefly showing the Home hero and then
    // swapping to Messages.
    navigate("/", { state: { openMessages: true } });
  };

  /*
   * Home nav item:
   * Navigating to "/" when we're already on "/" is a no-op for the
   * router (no remount happens), so the WhatsAppAuth page previously
   * stayed stuck on whatever view (e.g. Messages) it was already
   * showing. Dispatching this event lets it reset back to the landing
   * view in place, while a normal navigate() handles the cross-page case.
   */
  const handleHomeClick = () => {
    setMobileOpen(false);

    if (location.pathname === "/") {
      window.dispatchEvent(new Event("gy:go-home"));
      return;
    }

    navigate("/");
  };

  const isAdminPage = location.pathname.includes("admin");

  const userNavLinks = [
    { label: "Home", href: "/", isHome: true },
    ...(isLoggedIn ? [{ label: "Messages", href: "/", isMessages: true }] : []),
    { label: "About", href: "/about" },
    { label: "Contact", href: "/contact" },
  ];

  const adminNavLinks = [
    { label: "Dashboard", href: "/goyee-admin-dashboard" },
    { label: "Users", href: "/goyee-admin-users" }
  ];

  const navLinks = isAdminPage ? adminNavLinks : userNavLinks;

  const isLinkActive = (href) =>
    location.pathname === href ||
    (href === "/" && location.pathname === "/home");

  const goTo = (href) => {
    navigate(href);
    setMobileOpen(false);
  };

  // Routes a nav-link click either to the Messages panel, a Home
  // reset, or a normal route navigation, depending on the link.
  const handleNavLinkClick = (link) => {
    if (link.isMessages) {
      handleMessagesClick();
    } else if (link.isHome) {
      handleHomeClick();
    } else {
      goTo(link.href);
    }
  };

  return (
    <>
      <style>{`
        @keyframes gyLogoFloat {
          0%, 100% { transform: translateY(0px); }
          50%      { transform: translateY(-4px); }
        }
        @keyframes gyLogoGlow {
          0%, 100% { filter: drop-shadow(0 0 4px rgba(52,227,138,0.35)); }
          50%      { filter: drop-shadow(0 0 12px rgba(52,227,138,0.7)); }
        }
        .gy-logo-float {
          animation: gyLogoFloat 3.4s ease-in-out infinite,
                     gyLogoGlow 3.4s ease-in-out infinite;
        }
        .gy-logo-float:hover {
          animation-play-state: paused;
        }

        @keyframes gyPanelDown {
          0%   { opacity: 0; transform: translateY(-12px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes gyBackdropIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes gyLinkIn {
          0%   { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .gy-panel-in {
          animation: gyPanelDown 0.35s cubic-bezier(0.22,1,0.36,1) forwards;
        }
        .gy-backdrop-in {
          animation: gyBackdropIn 0.3s ease forwards;
        }
        .gy-link-in {
          opacity: 0;
          animation: gyLinkIn 0.35s cubic-bezier(0.22,1,0.36,1) forwards;
        }

        .gy-burger-line {
          transform-origin: center;
          transition: transform 0.3s cubic-bezier(0.22,1,0.36,1), opacity 0.25s ease;
        }
        .gy-burger-open .gy-burger-line-1 { transform: translateY(6px) rotate(45deg); }
        .gy-burger-open .gy-burger-line-2 { opacity: 0; }
        .gy-burger-open .gy-burger-line-3 { transform: translateY(-6px) rotate(-45deg); }

        .gy-nav-underline {
          background: linear-gradient(90deg, #34E38A, #0F7A45);
        }
        @keyframes gyFadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes gyScaleUp {
          0% { opacity: 0; transform: scale(0.95) translateY(10px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        .gy-fade-in {
          animation: gyFadeIn 0.3s ease forwards;
        }
        .gy-scale-up {
          animation: gyScaleUp 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
      `}</style>

      <nav
        className={`sticky top-0 z-50 w-full px-4 sm:px-6 lg:px-8 transition-all duration-300 ${
          scrolled ? "py-3 shadow-xl" : "py-4 shadow-md"
        }`}
        style={{
          background: scrolled
            ? "linear-gradient(135deg, rgba(4,32,20,0.92) 0%, rgba(6,48,29,0.92) 100%)"
            : "linear-gradient(135deg, rgba(5,38,24,0.85) 0%, rgba(8,56,34,0.85) 100%)",
          borderBottom: scrolled
            ? "1px solid rgba(52,227,138,0.3)"
            : "1px solid rgba(255,255,255,0.06)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
        }}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <div
            className="gy-logo-float text-xl sm:text-2xl font-extrabold shrink-0 cursor-pointer flex items-center gap-2 group"
            onClick={handleHomeClick}
          >
            <span
              className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center text-white text-base sm:text-lg shadow-md transition-transform duration-300 group-hover:rotate-12 group-hover:scale-110"
              style={{
                background: "linear-gradient(135deg, #34E38A, #22db91 55%, #0F7A45)",
              }}
            >
              G
            </span>
            <span
              className="bg-clip-text text-transparent transition-opacity duration-300"
              style={{
                backgroundImage: "linear-gradient(135deg, #6CFFB0, #22db91 60%, #34E38A)",
              }}
            >
              Goyee
            </span>
          </div>

          {/* Desktop Navigation */}
          <ul className="hidden md:flex items-center gap-10 font-medium">
            {navLinks.map((link) => {
              const isActive = isLinkActive(link.href);
              return (
                <li key={link.label} className="relative group">
                  <button
                    onClick={() => handleNavLinkClick(link)}
                    className={`py-1 transition-colors duration-300 ${
                      isActive ? "text-white font-bold" : "text-white/70 group-hover:text-white"
                    }`}
                  >
                    {link.label}
                  </button>
                  <span
                    className={`gy-nav-underline absolute left-0 -bottom-1 h-[2px] rounded-full transition-all duration-300 ${
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </li>
              );
            })}
          </ul>

          {/* Right Controls */}
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Desktop Controls */}
            <div className="hidden md:flex items-center gap-3">
              {!isAdminPage && (
                <button
                  onClick={handleConnect}
                  className="shrink-0 px-5 py-2 rounded-full font-semibold text-white transition-all duration-300 hover:scale-105 active:scale-95 shadow-md hover:shadow-[0_10px_24px_rgba(52,227,138,0.4)]"
                  style={{
                    background: "linear-gradient(135deg, #34E38A, #0F7A45)",
                    boxShadow: "0 8px 20px rgba(15,122,69,0.35)",
                  }}
                >
                  Connect
                </button>
              )}

              {isLoggedIn ? (
                <>
                  {!isAdminPage && (
                    <>
                      <button
                        onClick={() => {
                          setSuccessState(false);
                          setHelpModalOpen(true);
                        }}
                        className="shrink-0 flex items-center gap-2 px-5 py-2 rounded-full font-semibold text-white transition-all duration-300 hover:scale-105 active:scale-95 shadow-md hover:shadow-[0_10px_24px_rgba(52,227,138,0.4)]"
                        style={{
                          background: "linear-gradient(135deg, #34E38A, #0F7A45)",
                          boxShadow: "0 8px 20px rgba(15,122,69,0.35)",
                        }}
                      >
                        <FiHelpCircle size={18} />
                        <span>Get Help</span>
                      </button>
                      <button
                        onClick={() => goTo("/profile")}
                        className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 shadow-sm hover:scale-110"
                        style={{
                          backgroundColor: "rgba(52,227,138,0.14)",
                          color: "#6CFFB0",
                          border: "1px solid rgba(52,227,138,0.3)",
                        }}
                        title="Go to Profile"
                      >
                        <FiUser size={20} />
                      </button>
                    </>
                  )}

                  <button
                    onClick={handleLogout}
                    className="shrink-0 px-6 py-2 rounded-full font-semibold border-2 transition-all duration-300 hover:scale-105 active:scale-95"
                    style={{
                      backgroundColor: "transparent",
                      color: "#F87171",
                      borderColor: "#F87171",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#DC2626";
                      e.currentTarget.style.color = "#FFFFFF";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.color = "#F87171";
                    }}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <button
                  onClick={() => goTo("/userloginpage")}
                  className="shrink-0 px-6 py-2 rounded-full font-semibold text-white transition-all duration-300 hover:scale-105 active:scale-95 shadow-md hover:shadow-[0_10px_24px_rgba(52,227,138,0.4)]"
                  style={{
                    background: "linear-gradient(135deg, #6CFFB0, #22db91)",
                  }}
                >
                  Login
                </button>
              )}
            </div>

            {/* Mobile Connect (kept visible next to hamburger, as in the original) */}
            {!isAdminPage && (
              <button
                onClick={handleConnect}
                className="md:hidden px-4 py-2 rounded-full text-sm font-bold text-white transition-all duration-300 active:scale-95 shadow-md"
                style={{
                  background: "linear-gradient(135deg, #34E38A, #0F7A45)",
                }}
              >
                Connect
              </button>
            )}

            {/* Mobile Profile */}
            {isLoggedIn && !isAdminPage && (
              <button
                onClick={() => goTo("/profile")}
                className="md:hidden w-9 h-9 rounded-full flex items-center justify-center transition-colors shadow-sm"
                style={{
                  backgroundColor: "rgba(52,227,138,0.14)",
                  color: "#6CFFB0",
                  border: "1px solid rgba(52,227,138,0.3)",
                }}
                title="Go to Profile"
              >
                <FiUser size={18} />
              </button>
            )}

            {/* Mobile Hamburger */}
            <button
              onClick={() => setMobileOpen((prev) => !prev)}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              className={`md:hidden relative w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-200 ${
                mobileOpen ? "gy-burger-open" : ""
              }`}
              style={{ backgroundColor: "rgba(52,227,138,0.12)" }}
            >
              <svg width="22" height="16" viewBox="0 0 22 16" fill="none">
                <rect className="gy-burger-line gy-burger-line-1" y="0" width="22" height="2.2" rx="1.1" fill="#6CFFB0" />
                <rect className="gy-burger-line gy-burger-line-2" y="6.9" width="22" height="2.2" rx="1.1" fill="#6CFFB0" />
                <rect className="gy-burger-line gy-burger-line-3" y="13.8" width="22" height="2.2" rx="1.1" fill="#6CFFB0" />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile slide-down menu */}
        {mobileOpen && (
          <div
            className="gy-panel-in md:hidden absolute left-0 right-0 top-full mx-3 mt-2 rounded-2xl overflow-hidden shadow-2xl"
            style={{
              background: "linear-gradient(160deg, rgba(4,32,20,0.98) 0%, rgba(7,52,32,0.98) 100%)",
              border: "1px solid rgba(52,227,138,0.2)",
              backdropFilter: "blur(16px)",
            }}
            role="dialog"
            aria-modal="true"
          >
            <ul className="flex flex-col px-3 py-4 gap-1">
              {navLinks.map((link, i) => {
                const isActive = isLinkActive(link.href);
                return (
                  <li key={link.label} className="gy-link-in" style={{ animationDelay: `${i * 50}ms` }}>
                    <button
                      onClick={() => handleNavLinkClick(link)}
                      className={`w-full text-left px-4 py-3 rounded-xl font-semibold text-base transition-all duration-200 ${
                        isActive ? "text-white" : "text-white/70 hover:text-white"
                      }`}
                      style={{
                        backgroundColor: isActive ? "rgba(52,227,138,0.16)" : "transparent",
                      }}
                    >
                      {link.label}
                    </button>
                  </li>
                );
              })}
            </ul>

            <div
              className="px-3 pb-4 pt-3 border-t flex flex-col gap-3"
              style={{ borderColor: "rgba(255,255,255,0.08)" }}
            >
              {!isAdminPage && (
                <button
                  onClick={handleConnect}
                  className="gy-link-in w-full text-center px-4 py-3 rounded-xl font-semibold text-white transition-all duration-200 active:scale-95"
                  style={{
                    background: "linear-gradient(135deg, #34E38A, #0F7A45)",
                    animationDelay: "120ms",
                  }}
                >
                  Connect
                </button>
              )}

              {isLoggedIn ? (
                <div className="flex flex-col gap-3">
                  {!isAdminPage && (
                    <>
                      <button
                        onClick={() => {
                          setMobileOpen(false);
                          setSuccessState(false);
                          setHelpModalOpen(true);
                        }}
                        className="gy-link-in flex items-center justify-center gap-2 w-full px-6 py-3 rounded-full font-semibold transition-all duration-300 active:scale-95"
                        style={{
                          background: "linear-gradient(135deg, #34E38A, #0F7A45)",
                          color: "white",
                          animationDelay: "140ms",
                        }}
                      >
                        <FiHelpCircle size={18} />
                        Get Help
                      </button>
                      <button
                        onClick={() => goTo("/profile")}
                        className="gy-link-in flex items-center justify-center gap-2 w-full px-6 py-3 rounded-full font-semibold transition-all duration-300 active:scale-95"
                        style={{
                          border: "2px solid rgba(52,227,138,0.3)",
                          backgroundColor: "rgba(52,227,138,0.14)",
                          color: "#6CFFB0",
                          animationDelay: "170ms",
                        }}
                      >
                        <FiUser size={18} />
                        Profile
                      </button>
                    </>
                  )}
                  <button
                    onClick={handleLogout}
                    className="gy-link-in flex items-center justify-center gap-2 w-full px-6 py-3 rounded-full font-semibold border-2 transition-all duration-300 active:scale-95"
                    style={{
                      backgroundColor: "transparent",
                      color: "#F87171",
                      borderColor: "#F87171",
                      animationDelay: "220ms",
                    }}
                  >
                    <FiLogOut size={18} />
                    Logout
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => goTo("/userloginpage")}
                  className="gy-link-in flex items-center justify-center gap-2 w-full px-6 py-3.5 rounded-full font-semibold text-white transition-all duration-300 active:scale-95 shadow-md"
                  style={{
                    background: "linear-gradient(135deg, #6CFFB0, #22db91)",
                    animationDelay: "170ms",
                  }}
                >
                  Login
                </button>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Backdrop for mobile menu */}
      {mobileOpen && (
        <div
          className="gy-backdrop-in fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Floating Help Button */}
      {isLoggedIn && !isAdminPage && (
        <button
          onClick={() => {
            setSuccessState(false);
            setHelpModalOpen(true);
          }}
          className="fixed bottom-6 right-6 z-[100] w-14 h-14 bg-[#25D366] text-white rounded-full flex items-center justify-center shadow-lg hover:bg-[#128C4A] hover:scale-110 active:scale-95 transition-all duration-300 cursor-pointer"
          title="Get Help & Support"
        >
          <FiHelpCircle size={28} />
        </button>
      )}

      {/* Help & Support Modal */}
      {helpModalOpen && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm gy-fade-in">
          <div 
            className="bg-[#0E1613] border border-white/10 rounded-2xl shadow-2xl w-full max-w-md p-6 relative overflow-hidden transition-all transform gy-scale-up text-white"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {/* Close Button */}
            <button
              onClick={() => setHelpModalOpen(false)}
              className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors p-1 rounded-full hover:bg-white/5"
            >
              <FiX size={20} />
            </button>

            {successState ? (
              renderSuccess()
            ) : (
              <>
                <h3 className="text-xl font-bold text-white mb-6 font-['Space_Grotesk']">Help & Support</h3>

                {/* Tabs Header */}
                <div className="flex border-b border-white/10 mb-6">
                  <button
                    type="button"
                    onClick={() => setActiveTab("report")}
                    className={`flex-1 pb-3 text-sm font-semibold transition-colors relative ${
                      activeTab === "report" ? "text-[#25D366]" : "text-white/50 hover:text-white"
                    }`}
                  >
                    Report an Issue
                    {activeTab === "report" && (
                      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#25D366]" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab("feedback")}
                    className={`flex-1 pb-3 text-sm font-semibold transition-colors relative ${
                      activeTab === "feedback" ? "text-[#25D366]" : "text-white/50 hover:text-white"
                    }`}
                  >
                    Share Feedback
                    {activeTab === "feedback" && (
                      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#25D366]" />
                    )}
                  </button>
                </div>

                {/* Tab Forms */}
                <form onSubmit={handleSupportSubmit} className="space-y-4">
                  {activeTab === "report" ? (
                    <>
                      {/* Issue Type */}
                      <div className="space-y-1.5">
                        <label className="block text-xs font-semibold text-white/50 uppercase tracking-wide">
                          Issue Type *
                        </label>
                        <select
                          value={issueType}
                          onChange={(e) => setIssueType(e.target.value)}
                          className="w-full px-4 py-2.5 bg-[#121A16] border border-[#1E2822] rounded-xl text-sm text-white outline-none focus:bg-white/[0.02] focus:border-[#25D366] transition-all"
                        >
                          <option value="WhatsApp Connection" className="bg-[#0E1613] text-white">WhatsApp Connection</option>
                          <option value="Message Sending" className="bg-[#0E1613] text-white">Message Sending</option>
                          <option value="Bulk Messaging" className="bg-[#0E1613] text-white">Bulk Messaging</option>
                          <option value="Scheduled Messages" className="bg-[#0E1613] text-white">Scheduled Messages</option>
                          <option value="Account/Login" className="bg-[#0E1613] text-white">Account/Login</option>
                          <option value="Credits" className="bg-[#0E1613] text-white">Credits</option>
                          <option value="Bug Report" className="bg-[#0E1613] text-white">Bug Report</option>
                          <option value="Other" className="bg-[#0E1613] text-white">Other</option>
                        </select>
                      </div>

                      {/* Description */}
                      <div className="space-y-1.5">
                        <label className="block text-xs font-semibold text-white/50 uppercase tracking-wide">
                          Description *
                        </label>
                        <textarea
                          required
                          rows="4"
                          value={issueDescription}
                          onChange={(e) => setIssueDescription(e.target.value)}
                          placeholder="Please describe the issue in detail..."
                          className="w-full px-4 py-2.5 bg-[#121A16] border border-[#1E2822] rounded-xl text-sm text-white placeholder:text-white/30 outline-none focus:bg-white/[0.02] focus:border-[#25D366] transition-all resize-none"
                        />
                      </div>

                      {/* Screenshot Upload */}
                      <div className="space-y-1.5">
                        <label className="block text-xs font-semibold text-white/50 uppercase tracking-wide">
                          Screenshot (Optional)
                        </label>
                        <div className="relative border border-dashed border-[#1E2822] rounded-xl px-4 py-3 flex items-center justify-between hover:border-[#25D366] hover:bg-[#25D366]/5 transition-all cursor-pointer">
                          <span className="text-xs text-white/50 truncate pr-4">
                            {screenshot ? screenshot.filename : "Upload screenshot..."}
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleScreenshotChange}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          />
                          <button
                            type="button"
                            className="text-xs text-[#25D366] font-semibold shrink-0"
                          >
                            Browse
                          </button>
                        </div>
                      </div>

                      {/* Submit button */}
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full py-3 bg-[#25D366] hover:bg-[#128C4A] disabled:opacity-50 text-white font-bold rounded-xl shadow-md transition-all mt-4 flex items-center justify-center"
                      >
                        {submitting ? "Submitting..." : "Submit Report"}
                      </button>
                    </>
                  ) : (
                    <>
                      {/* Rating */}
                      <div className="space-y-1.5 text-center">
                        <label className="block text-xs font-semibold text-white/50 uppercase tracking-wide text-left">
                          Rating *
                        </label>
                        {renderStars()}
                      </div>

                      {/* Feedback Text */}
                      <div className="space-y-1.5">
                        <label className="block text-xs font-semibold text-white/50 uppercase tracking-wide">
                          Feedback *
                        </label>
                        <textarea
                          required
                          rows="4"
                          value={feedbackText}
                          onChange={(e) => setFeedbackText(e.target.value)}
                          placeholder="Tell us how we can improve Goyee..."
                          className="w-full px-4 py-2.5 bg-[#121A16] border border-[#1E2822] rounded-xl text-sm text-white placeholder:text-white/30 outline-none focus:bg-white/[0.02] focus:border-[#25D366] transition-all resize-none"
                        />
                      </div>

                      {/* Submit button */}
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full py-3 bg-[#25D366] hover:bg-[#128C4A] disabled:opacity-50 text-white font-bold rounded-xl shadow-md transition-all mt-4 flex items-center justify-center"
                      >
                        {submitting ? "Submitting..." : "Submit Feedback"}
                      </button>
                    </>
                  )}
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}